import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Passo } from './passos'
import './guia.scss'

interface Posicao { foco: { top: number; left: number; width: number; height: number } | null; top: number; left: number }

const LARGURA = 340
const FOLGA = 8
const MARGEM = 12

/** O elemento que o passo destaca, se estiver na tela (no celular o menu lateral fica escondido). */
export function acharAlvo(alvo?: string): HTMLElement | null {
  if (!alvo) return null
  const el = document.querySelector<HTMLElement>(`[data-guia="${alvo}"]`)
  if (!el || el.getClientRects().length === 0) return null
  const r = el.getBoundingClientRect()
  return r.right > 0 && r.left < window.innerWidth && r.bottom > 0 && r.top < window.innerHeight ? el : null
}

/** Os passos que dá para mostrar agora: os sem alvo e os cujo alvo está visível. */
export function passosNaTela(passos: Passo[]): Passo[] {
  return passos.filter(p => !p.alvo || acharAlvo(p.alvo))
}

function posicionar(alvo: HTMLElement | null, altura: number): Posicao {
  const largura = Math.min(LARGURA, window.innerWidth - 2 * MARGEM)
  if (!alvo) {
    return { foco: null, left: Math.max(MARGEM, (window.innerWidth - largura) / 2), top: Math.max(MARGEM, (window.innerHeight - altura) / 2) }
  }
  const r = alvo.getBoundingClientRect()
  const foco = { top: r.top - FOLGA / 2, left: r.left - FOLGA / 2, width: r.width + FOLGA, height: r.height + FOLGA }
  // À direita do alvo (o menu fica na esquerda); sem espaço, abaixo ou acima dele.
  let left = r.right + 16
  let top = r.top + r.height / 2 - altura / 2
  if (left + largura > window.innerWidth - MARGEM) {
    left = Math.min(Math.max(MARGEM, r.left), window.innerWidth - largura - MARGEM)
    top = r.bottom + 14
    if (top + altura > window.innerHeight - MARGEM) top = Math.max(MARGEM, r.top - altura - 14)
  }
  top = Math.min(Math.max(MARGEM, top), Math.max(MARGEM, window.innerHeight - altura - MARGEM))
  return { foco, left, top }
}

interface Props {
  passos: Passo[]
  /** Fecha o guia; concluiu é verdadeiro quando a pessoa chegou ao fim (e falso quando pulou). */
  aoFechar: (concluiu: boolean) => void
}

/** Guia de boas-vindas: escurece a tela, destaca um item do portal por vez e explica para que serve. */
export default function GuiaDoPortal({ passos: todos, aoFechar }: Props) {
  const [passos] = useState(() => passosNaTela(todos))
  const [i, setI] = useState(0)
  const [pos, setPos] = useState<Posicao | null>(null)
  const caixa = useRef<HTMLDivElement>(null)
  const principal = useRef<HTMLButtonElement>(null)
  const passo = passos[i]
  const ultimo = i === passos.length - 1

  const medir = useCallback(() => {
    if (!passo) return
    setPos(posicionar(acharAlvo(passo.alvo), caixa.current?.offsetHeight || 190))
  }, [passo])

  useLayoutEffect(() => {
    acharAlvo(passo?.alvo)?.scrollIntoView?.({ block: 'nearest' })
    medir()
    // A caixa só sabe a própria altura depois de desenhar o texto do passo.
    const t = requestAnimationFrame(medir)
    return () => cancelAnimationFrame(t)
  }, [passo, medir])

  useEffect(() => {
    window.addEventListener('resize', medir)
    window.addEventListener('scroll', medir, true)
    return () => { window.removeEventListener('resize', medir); window.removeEventListener('scroll', medir, true) }
  }, [medir])

  useEffect(() => { principal.current?.focus() }, [i])

  const avancar = useCallback(() => (ultimo ? aoFechar(true) : setI(n => n + 1)), [ultimo, aoFechar])
  const voltar = () => setI(n => Math.max(0, n - 1))

  useEffect(() => {
    const teclas = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); aoFechar(false) }
      else if (e.key === 'ArrowRight') { e.preventDefault(); avancar() }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setI(n => Math.max(0, n - 1)) }
      else if (e.key === 'Tab' && caixa.current) {
        // O foco fica preso nos botões do guia enquanto ele está aberto.
        const botoes = Array.from(caixa.current.querySelectorAll<HTMLElement>('button'))
        if (botoes.length === 0) return
        const primeiro = botoes[0], final = botoes[botoes.length - 1]
        if (e.shiftKey && document.activeElement === primeiro) { e.preventDefault(); final.focus() }
        else if (!e.shiftKey && document.activeElement === final) { e.preventDefault(); primeiro.focus() }
      }
    }
    window.addEventListener('keydown', teclas)
    return () => window.removeEventListener('keydown', teclas)
  }, [avancar, aoFechar])

  if (!passo) return null

  return (
    <div className="np-guia" data-testid="guia">
      <div className={`np-guia-bloqueio${pos?.foco ? '' : ' escuro'}`} onClick={e => e.stopPropagation()} />
      {pos?.foco && <div className="np-guia-foco" style={pos.foco} aria-hidden="true" />}
      <div ref={caixa} className="np-guia-caixa" role="dialog" aria-modal="true" aria-labelledby="np-guia-titulo" aria-describedby="np-guia-texto"
           style={{ left: pos?.left ?? MARGEM, top: pos?.top ?? MARGEM, width: Math.min(LARGURA, window.innerWidth - 2 * MARGEM), visibility: pos ? 'visible' : 'hidden' }}>
        <div className="np-guia-passo">Passo {i + 1} de {passos.length}</div>
        <h2 id="np-guia-titulo">{passo.titulo}</h2>
        <p id="np-guia-texto">{passo.texto}</p>
        {passo.dicas && <ul>{passo.dicas.map(d => <li key={d}>{d}</li>)}</ul>}
        <div className="np-guia-pontos" aria-hidden="true">
          {passos.map((p, n) => <i key={p.id} className={n === i ? 'atual' : n < i ? 'feito' : undefined} />)}
        </div>
        <div className="np-guia-botoes">
          {!ultimo && <button type="button" className="np-guia-pular" onClick={() => aoFechar(false)}>Pular guia</button>}
          {i > 0 && <button type="button" className="np-guia-voltar" onClick={voltar}>Voltar</button>}
          <button ref={principal} type="button" className="np-guia-proximo" onClick={avancar}>{ultimo ? 'Começar a explorar' : i === 0 ? 'Vamos lá' : 'Próximo'}</button>
        </div>
      </div>
    </div>
  )
}
